import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { AfslTemplate, EmailTemplate, EntityLookup, PracticeTemplate, SavedParameterWithAfsl, SchedulerState } from './types';
import { fetchAfsls, fetchFilteredReportClients } from 'src/features/reporting/store/thunks';
import { AfslViewModel, FilteredReportClientViewModel, GetFilteredClientEntity, ScheduledEvent } from 'src/features/reporting/store/types';
import { fetchAfslTemplates, fetchPracticeTemplates, fetchScheduledEventById } from './thunks';
import { fetchReportParametersList } from 'src/features/clients/client/reportsV2/store/thunks';
import { SavedParameter } from 'src/features/clients/client/reportsV2/store/types';
import { fetchAccountSearchResults } from 'src/features/bulk/email/search/store/thunks';
import { AccountSearchResult } from 'src/features/bulk/email/search/store/types';

const initialState: SchedulerState = {
  afsls: [],
  clients: [],
  practices: [],
  advisers: [],
  groups: [],
  knownEntities: { afsls: {}, practices: {}, advisers: {}, groups: {}, clients: {} },
  savedParametersList: [],
  savedParameter: null,
  editingEvent: undefined,
  accountSearchResult: [],
  selectedTemplate: undefined,
  allowStaticClients: false,
  afslTemplates: [],
  practiceTemplates: [],
};

const remember = (lookup: EntityLookup, entities: GetFilteredClientEntity[]) => {
  entities.forEach((entity) => {
    lookup[entity.entityCoreId] = entity;
  });
};

export const schedulerSlice = createSlice({
  name: '@@scheduler',
  initialState,
  reducers: {
    resetEditingSchedulerEvent: (state) => {
      state.editingEvent = undefined;
      state.selectedTemplate = undefined;
    },
    setSavedParameter: (state, action: PayloadAction<SavedParameterWithAfsl | null>) => {
      state.savedParameter = action.payload;
    },
    setAllowStaticClients: (state, action: PayloadAction<boolean>) => {
      state.allowStaticClients = action.payload;
    },
    setSelectedTemplate: (state, action: PayloadAction<EmailTemplate | undefined>) => {
      state.selectedTemplate = action.payload;
    },
    resetEmailTemplates: (state) => {
      state.afslTemplates = [];
      state.practiceTemplates = [];
    },
    rememberClients: (state, action: PayloadAction<GetFilteredClientEntity[]>) => {
      remember(state.knownEntities.clients, action.payload);
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchFilteredReportClients.fulfilled, (state, action: PayloadAction<FilteredReportClientViewModel>) => {
      state.clients = action.payload.clients;
      state.afsls = action.payload.afsls;
      state.advisers = action.payload.advisers;
      state.groups = action.payload.groups;
      state.practices = action.payload.practices;
      remember(state.knownEntities.afsls, action.payload.afsls);
      remember(state.knownEntities.practices, action.payload.practices);
      remember(state.knownEntities.advisers, action.payload.advisers);
      remember(state.knownEntities.groups, action.payload.groups);
      remember(state.knownEntities.clients, action.payload.clients);
    });
    builder.addCase(fetchAfsls.fulfilled, (state, action: PayloadAction<AfslViewModel>) => {
      (action.payload?.afsls ?? []).forEach((afsl) => {
        remember(state.knownEntities.afsls, [{ entityCoreId: afsl.afslId, name: afsl.afslName }]);
        (afsl.groups ?? []).forEach((group) => remember(state.knownEntities.groups, [{ entityCoreId: group.groupId, name: group.groupName }]));
        (afsl.practices ?? []).forEach((practice) => {
          remember(state.knownEntities.practices, [{ entityCoreId: practice.practiceId, name: practice.practiceName }]);
          (practice.advisors ?? []).forEach((adviser) =>
            remember(state.knownEntities.advisers, [{ entityCoreId: adviser.advisorId, name: adviser.advisorName }])
          );
        });
      });
    });
    builder.addCase(fetchScheduledEventById.pending, (state) => {
      state.editingEvent = null;
    });
    builder.addCase(fetchScheduledEventById.fulfilled, (state, action: PayloadAction<ScheduledEvent>) => {
      state.editingEvent = action.payload;
    });
    builder.addCase(fetchAfslTemplates.pending, (state) => {
      state.afslTemplates = [];
    });
    builder.addCase(fetchAfslTemplates.fulfilled, (state, action: PayloadAction<AfslTemplate[]>) => {
      state.afslTemplates = action.payload;
    });
    builder.addCase(fetchPracticeTemplates.pending, (state) => {
      state.practiceTemplates = [];
    });
    builder.addCase(fetchPracticeTemplates.fulfilled, (state, action: PayloadAction<PracticeTemplate[]>) => {
      state.practiceTemplates = action.payload;
    });
    builder.addCase(fetchReportParametersList.fulfilled, (state, action: PayloadAction<SavedParameter[]>) => {
      state.savedParametersList = action.payload.filter(
        (param: SavedParameter, index: number, self: SavedParameter[]) => index === self.findIndex((p) => p.id === param.id)
      );
    });
    builder.addCase(fetchAccountSearchResults.fulfilled, (state, action: PayloadAction<AccountSearchResult[]>) => {
      state.accountSearchResult = action.payload;
    });
  },
});
